import * as React from "react"

// Breakpoint constants based on requirements
const BREAKPOINTS = {
  mobile: 320,
  tablet: 768,
  desktop: 1024,
} as const

export interface DeviceInfo {
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean
  screenSize: 'mobile' | 'tablet' | 'desktop'
  orientation: 'portrait' | 'landscape'
  width: number
  height: number
}

export function useDeviceInfo(): DeviceInfo {
  const [deviceInfo, setDeviceInfo] = React.useState<DeviceInfo>(() => {
    // Initialize with safe defaults for SSR
    if (typeof window === 'undefined') {
      return {
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        screenSize: 'desktop',
        orientation: 'landscape',
        width: 1024,
        height: 768,
      }
    }

    const width = window.innerWidth
    const height = window.innerHeight
    const isMobile = width < BREAKPOINTS.tablet
    const isTablet = width >= BREAKPOINTS.tablet && width < BREAKPOINTS.desktop
    const isDesktop = width >= BREAKPOINTS.desktop
    const orientation = width > height ? 'landscape' : 'portrait'
    
    let screenSize: 'mobile' | 'tablet' | 'desktop' = 'desktop'
    if (isMobile) screenSize = 'mobile'
    else if (isTablet) screenSize = 'tablet'

    return {
      isMobile,
      isTablet,
      isDesktop,
      screenSize,
      orientation,
      width,
      height,
    }
  })

  React.useEffect(() => {
    const updateDeviceInfo = () => {
      const width = window.innerWidth
      const height = window.innerHeight
      const isMobile = width < BREAKPOINTS.tablet
      const isTablet = width >= BREAKPOINTS.tablet && width < BREAKPOINTS.desktop
      const isDesktop = width >= BREAKPOINTS.desktop
      const orientation = width > height ? 'landscape' : 'portrait'
      
      let screenSize: 'mobile' | 'tablet' | 'desktop' = 'desktop'
      if (isMobile) screenSize = 'mobile'
      else if (isTablet) screenSize = 'tablet'

      setDeviceInfo({
        isMobile,
        isTablet,
        isDesktop,
        screenSize,
        orientation,
        width,
        height,
      })
    }

    // Create media query listeners for each breakpoint
    const mobileQuery = window.matchMedia(`(max-width: ${BREAKPOINTS.tablet - 1}px)`)
    const tabletQuery = window.matchMedia(`(min-width: ${BREAKPOINTS.tablet}px) and (max-width: ${BREAKPOINTS.desktop - 1}px)`)
    const desktopQuery = window.matchMedia(`(min-width: ${BREAKPOINTS.desktop}px)`)
    const orientationQuery = window.matchMedia('(orientation: portrait)')

    // Add event listeners
    mobileQuery.addEventListener('change', updateDeviceInfo)
    tabletQuery.addEventListener('change', updateDeviceInfo)
    desktopQuery.addEventListener('change', updateDeviceInfo)
    orientationQuery.addEventListener('change', updateDeviceInfo)
    window.addEventListener('resize', updateDeviceInfo)

    // Initial update
    updateDeviceInfo()

    // Cleanup
    return () => {
      mobileQuery.removeEventListener('change', updateDeviceInfo)
      tabletQuery.removeEventListener('change', updateDeviceInfo)
      desktopQuery.removeEventListener('change', updateDeviceInfo)
      orientationQuery.removeEventListener('change', updateDeviceInfo)
      window.removeEventListener('resize', updateDeviceInfo)
    }
  }, [])

  return deviceInfo
}